import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Css, H6, Responsive } from '../../../../ui';
import LocationSearchShimmer from './LocationSearchShimmer';
import { Color } from '../../../../styles/Color';
import { PoiStore } from '../../../shop-dine-relax/search/domain/hooks/usePoiStore';
import PoiCard from '../../../shop-dine-relax/ui/PoiCard';
import { StyledCardsContainer } from '../../../../components/common/cards/CardsContainer';
import { getPoiCardIcon } from '../../../shop-dine-relax/search/domain/utils/getPoiCardIcon';
import { QuickLink } from '../../../../components/common/quick-links/QuickLink';
import { getShopDineRelaxListUrl } from '../../../shop-dine-relax/use-cases/getShopDineRelaxListUrl';

interface ILocationSearchListProps {
	store: PoiStore;
	query?: string;
	isDebouncing: boolean;
}

export const LocationSearchList = observer(
	({ store, query, isDebouncing }: ILocationSearchListProps) => {
		const {
			data: { filteredData, loading, error },
		} = store;
		const showLoading = loading || isDebouncing;

		const locationsCount = showLoading ? '' : `(${filteredData.length})`;
		const locationsPageQuery = filteredData.length > 0 ? query : undefined;
		const locationsPageUrl = getShopDineRelaxListUrl(
			{
				query: locationsPageQuery,
			},
			false,
		);

		if (error && !showLoading) return null; // error state
		if (filteredData.length === 0 && !showLoading) return null; // emtpy state
		return (
			<div>
				<Heading>
					<ListTitle>Locations {locationsCount}</ListTitle>
					<StyledLink type="internal" url={locationsPageUrl}>
						Go to Shop & Dine
					</StyledLink>
				</Heading>
				{showLoading ? (
					<LocationSearchShimmer />
				) : (
					<PoiContainer>
						{filteredData.map((poi) => (
							<PoiCard key={poi.id} icon={getPoiCardIcon(poi)} {...poi} />
						))}
					</PoiContainer>
				)}
			</div>
		);
	},
);

const Heading = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: ${Css.pxToRem(12)};
`;

const StyledLink = styled(QuickLink)`
	margin: 0;
`;

const ListTitle = styled(H6)`
	margin-bottom: 0;
	color: ${Color.PrimaryBlue};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;

export const PoiContainer = styled(StyledCardsContainer)`
	width: 100%;
	grid-template-columns: repeat(2, 1fr);
	gap: ${Css.pxToRem(24)};
	> li {
		padding: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		grid-template-columns: repeat(1, 1fr);
		gap: ${Css.pxToRem(10)};
	}
`;
